import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import { urlAdvancedExportDocuments, urlAdvancedExportConfig } from '../../../Constants';
import { downloadDocumentsStream } from '../../../util/FileUtils';
import { getExportConfig } from './sliceProjectAdvancedExport';
import { IExportAdditionalDocument } from '../../../components/General/Dialogs/_parts/AdvancedExport';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectAdvancedExportDialogState = {
    isDialogOpen: false,
    isSingleProtocolDocumentExported: false,
    downloadedDocuments: 0,
    loading: false,
    error: null,
    downloadLink: null,
};

const reducers = {
    openAdvancedExportDialog(state: IProjectAdvancedExportDialogState) {
        state.isDialogOpen = true;
    },

    setIsSingleProtocolDocumentExported(state: IProjectAdvancedExportDialogState) {
        state.isSingleProtocolDocumentExported = true;
    },

    closeAdvancedExportDialog(state: IProjectAdvancedExportDialogState) {
        state.isDialogOpen = false;
    },

    resetAdvanceExportDownloadedDocuments(state: IProjectAdvancedExportDialogState) {
        state.downloadedDocuments = 0;
        state.isSingleProtocolDocumentExported = false;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get data for Advanced Export Documents Dialog
     */
    getAdvancedExportConfig: async (config: { replicaId: string }) => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlAdvancedExportConfig, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onAdvancedExportDocuments: async (
        config: {
            fileName: string;
            replicaId: string;
            documentTypeName: string;
            includeInExport: string[];
            additionalFiles: IExportAdditionalDocument[];
            universalId?: string;
            protocolOnly?: boolean;
        },
        thunkApi,
    ): Promise<number> => {
        try {
            const {
                replicaId,
                documentTypeName,
                includeInExport,
                additionalFiles,
                fileName,
                universalId,
                protocolOnly,
            } = config;

            const { documentIds } = (thunkApi.getState() as IAppReduxStore).projectSelectedDocuments
                .storage;

            const exportConfig = getExportConfig({
                additionalFiles,
                includeInExport,
                documentTypeName,
                protocolOnly,
            });

            const universalIds = [universalId ?? documentIds].flat();

            const formData = new FormData();
            formData.append('universalIds', universalIds.join('|'));
            formData.append('exportconfig', JSON.stringify(exportConfig));

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlAdvancedExportDocuments, request);

            if (response.ok) {
                downloadDocumentsStream(await response.blob(), fileName);
                return Promise.resolve(universalIds.length);
            }

            return Promise.reject(`${fileName}.zip`);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectAdvancedExportDialogState>) => {
        /**
         * Reducers for the 'onConfirmExportDocuments' thunk.
         */
        builder.addCase(
            onAdvancedExportDocuments.pending,
            (state: Draft<IProjectAdvancedExportDialogState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onAdvancedExportDocuments.fulfilled,
            (state: Draft<IProjectAdvancedExportDialogState>, action: IAction<number>) => {
                state.downloadedDocuments = action.payload;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onAdvancedExportDocuments.rejected,
            (state: Draft<IProjectAdvancedExportDialogState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getAdvancedExportConfig = createAsyncThunk(
    `${urlAdvancedExportConfig}`,
    thunks.getAdvancedExportConfig,
);

export const onAdvancedExportDocuments = createAsyncThunk(
    `${urlAdvancedExportDocuments}`,
    thunks.onAdvancedExportDocuments,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectAdvancedExportDialogState extends SliceProps {
    isDialogOpen: boolean;
    isSingleProtocolDocumentExported: boolean;
    downloadedDocuments: number;
    downloadLink: string;
}

/**
 * Create the redux slice.
 */
export const projectAdvancedExportDocumentsDataSlice = createSlice({
    name: 'projectAdvancedExportDialogData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    closeAdvancedExportDialog,
    resetAdvanceExportDownloadedDocuments,
    openAdvancedExportDialog,
    setIsSingleProtocolDocumentExported,
} = projectAdvancedExportDocumentsDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectAdvancedExportDocumentsDataSlice.reducer;
